import { useNavigate } from 'react-router-dom';
import { DataTable, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { ActionIcon } from '@repletoryreach/sdk-ui';
import { Menu } from '@repletoryreach/sdk-ui/overlays';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate, formatInteger, formatRelative } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { TenantAvatar } from './TenantAvatar';
import { useT } from '../../../i18n/I18nProvider';

type Tenant = NonNullable<Awaited<ReturnType<typeof admin.listTenants>>['data']>[number];

/**
 * The organisation directory.
 *
 * The reference design carries a `TEN-000128` reference column; we have no
 * such sequential id, only a UUID, and a UUID is not a label. The
 * organisation's name is the identifier here, with the slug beneath it, so the
 * column the design used for a reference is the column that actually
 * identifies the row.
 */
export function TenantsTable({ records, loading }: { records: Tenant[]; loading: boolean }) {
  const navigate = useNavigate();
  const t = useT();

  return (
    <DataTable<Indexed<Tenant>>
      records={records as Indexed<Tenant>[]}
      loading={loading}
      idAccessor="id"
      emptyState={t('tenants.table.empty')}
      onRowClick={(row) => navigate(`/tenants/${row.id}`)}
      columns={[
        {
          accessor: 'name',
          title: t('tenants.columns.organisation'),
          sortable: true,
          render: (r) => (
            <TwoLineCell
              primary={r.name}
              secondary={r.slug}
              title={r.name}
              leading={<TenantAvatar name={r.name} />}
            />
          )
        },
        {
          accessor: 'user_count',
          title: t('tenants.columns.users'),
          textAlign: 'right',
          sortable: true,
          render: (r) => formatInteger(r.user_count)
        },
        {
          accessor: 'is_active',
          title: t('common.status'),
          // The active/inactive split is this page's primary KPI, so it carries
          // a semantic tone rather than a word in a neutral pill.
          render: (r) => (
            <StatusBadge size="xs" status={r.is_active ? 'healthy' : 'inactive'}>
              {r.is_active ? t('status.active') : t('status.inactive')}
            </StatusBadge>
          )
        },
        {
          accessor: 'country_iso',
          title: t('tenants.columns.country'),
          sortable: true,
          // The design pairs the country with its timezone in one cell rather
          // than spending two columns on one fact about where a tenant sits.
          render: (r) => <TwoLineCell primary={r.country_iso} secondary={r.timezone} title={r.timezone} />
        },
        {
          accessor: 'created_at',
          title: t('common.created'),
          sortable: true,
          // The design shows "Last activity"; the tenants endpoint carries no
          // such column, so this says what it can honestly say.
          render: (r) => <TwoLineCell primary={formatDate(r.created_at)} secondary={formatRelative(r.created_at)} />
        },
        {
          accessor: 'actions',
          title: t('common.actions'),
          width: 56,
          render: (r) => <TenantRowMenu tenant={r} />
        }
      ]}
    />
  );
}

function TenantRowMenu({ tenant }: { tenant: Tenant }) {
  const t = useT();
  const navigate = useNavigate();
  const unavailable = t('tenants.row.lifecycleUnavailable');

  function go(to: string) {
    navigate(to);
  }

  return (
    // The row itself navigates, so the menu swallows its own clicks.
    <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <Menu position="bottom-end" width={210}>
        <Menu.Target>
          <ActionIcon variant="subtle" size="sm" aria-label={t('tenants.row.menuLabel', { name: tenant.name })}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
              <circle cx="3" cy="8" r="1.4" /><circle cx="8" cy="8" r="1.4" /><circle cx="13" cy="8" r="1.4" />
            </svg>
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => go(`/tenants/${tenant.id}`)}>{t('tenants.row.viewDetails')}</Menu.Item>
          <Menu.Item onClick={() => go(`/tenants/${tenant.id}?tab=users`)}>{t('tenants.row.manageUsers')}</Menu.Item>
          <Menu.Item onClick={() => go(`/tenants/${tenant.id}?tab=billing`)}>{t('tenants.row.viewBilling')}</Menu.Item>
          <Menu.Divider />
          {/* Lifecycle endpoints do not exist yet. A confirm dialog that
              resolves to "not implemented" teaches operators that confirms mean
              nothing, so these stay visibly out of reach instead. */}
          <span title={unavailable}>
            <Menu.Item disabled>
              {tenant.is_active ? t('tenants.row.suspend') : t('tenants.row.reactivate')}
            </Menu.Item>
          </span>
          <span title={unavailable}>
            <Menu.Item disabled color="error">{t('tenants.row.delete')}</Menu.Item>
          </span>
        </Menu.Dropdown>
      </Menu>
    </span>
  );
}
